import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import {  Box, Button, Divider} from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import { useVehicleDetailContext } from "contexts/VehicleDetailContext";
import FormDrawer from "components/Shared/FormDrawer";
import VehicleInsuranceForm from "screens/App/CRM/Vehicle/vehicleComponents/VehicleInsuranceForm";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";

import {
  useAddVehicleInsuranceMutation,
  useUpdateVehicleInsuranceMutation,
} from "store/services/vehicleApi";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

const VehicleInsuranceTabs = () => {
  // Get vehicle detail from context (assuming a similar context exists as driverDetail)
  const { vehicleDetail } = useVehicleDetailContext();
  // All insurance records from the vehicle detail context
  const vehicleInsurances = useMemo(() => vehicleDetail?.vehicleInsurance ?? [], [vehicleDetail?.vehicleInsurance]);

  // State for controlling the form drawer
  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState(null);

  // Toggle filter state: "active" or "inactive"
  const [insuranceFilter, setInsuranceFilter] = useState("Active");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(5);

  // Example flag if the vehicle stage disallows editing
  const disableInsuranceResponse = vehicleDetail?.stage === "Closed";

  // RTK Query mutations for add, update
  const [addVehicleInsurance, { isLoading: addingInsurance }] =
    useAddVehicleInsuranceMutation();
  const [updateVehicleInsurance, { isLoading: updatingInsurance }] =
    useUpdateVehicleInsuranceMutation();

  // Filter the insurance records based on the toggle value.
  const filteredInsurances = useMemo(() => {
    return vehicleInsurances.filter(
      (insurance) => insurance.status === (insuranceFilter === "Active")
    );
  }, [vehicleInsurances, insuranceFilter]);

  // Sort insurances (most recent first by start_date)
  const sortedInsurances = useMemo(() => {
    return [...filteredInsurances].sort(
      (a, b) =>
        new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
    );
  }, [filteredInsurances]);

  // Paginate the sorted insurances
  const paginatedInsurances = useMemo(() => {
    return sortedInsurances.slice(
      (currentPage - 1) * perPage,
      currentPage * perPage
    );
  }, [sortedInsurances, currentPage, perPage]);

  // Handler to cancel the form drawer
  const handleCancelInsurance = () => {
    setShowInsuranceForm(false);
    setSelectedInsurance(null);
  };

  // Handler to add new insurance record
  const handleAddInsurance = async (data) => {
    try {
      await addVehicleInsurance({
        ...data,
        vehicle_id: vehicleDetail?._id,
      }).unwrap();
      toast.success("New Insurance record added!");
      handleCancelInsurance();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't add new insurance record!");
    }
  };

  // Handler to update an existing insurance record
  const handleUpdateInsurance = async (data) => {
    try {
      await updateVehicleInsurance({
        ...data,
        vehicle_id: vehicleDetail?._id,
        _id: selectedInsurance?._id,
      }).unwrap();
      toast.success("Insurance record updated successfully!");
      handleCancelInsurance();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update insurance record!");
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => setCurrentPage(page);
  const handlePerRowsChange = (newPerPage) => setPerPage(newPerPage);

  // Handler for the toggle button change
  const handleToggleChange = (event, newFilter) => {
    if (newFilter !== null) {
      setInsuranceFilter(newFilter);
      setCurrentPage(1); // Reset to first page on filter change
    }
  };

  // Define columns for the AppTable
  const columns = [
    {
      name: "Merchant",
      selector: (row) => row?.merchant?.merchantName,
      cell: (row) => row?.merchant?.merchantName || "N/A",
    },
    {
      name: "Insurance ID",
      selector: (row) => row.insurance_id,
      cell: (row) => row.insurance_id || "N/A",
    },
    {
      name: "Policy Number",
      selector: (row) => row.policy_number,
      cell: (row) =>
        row.policy_number ? (
          <HighlightedText text={row.policy_number} highlight={""} />
        ) : (
          "N/A"
        ),
    },
    {
      name: "Premium",
      selector: (row) => row.premium_amount,
      cell: (row) =>
        row.premium_amount != null ? `Rs.${row.premium_amount}` : "N/A",
    },

    {
      name: "Start Date",
      selector: (row) => row.start_date,
      cell: (row) => (
        <span>
          {row.start_date
            ? new Date(row.start_date).toLocaleDateString()
            : "N/A"}
        </span>
      ),
    },
    {
      name: "Expiry Date",
      selector: (row) => row.expiry_date,
      cell: (row) => (
        <span>
          {row.expiry_date
            ? new Date(row.expiry_date).toLocaleDateString()
            : "N/A"}
        </span>
      ),
    },

    {
      name: "Actions",
      cell: (row) => (
        <Button
          onClick={() => {
            setSelectedInsurance(row);
            setShowInsuranceForm(true);
          }}
          disabled={disableInsuranceResponse}
        >
          <Edit />
        </Button>
      ),
    },
  ];

  return (
    <>
      <Box display="flex" justifyContent="end" padding={3}>
        <Box display="flex" alignItems="center" gap={2}>
          {/* Toggle Button Group for Active / Inactive */}
          <ToggleButtonGroup
            value={insuranceFilter}
            exclusive
            onChange={handleToggleChange}
            aria-label="Insurance filter"
          >
            <ToggleButton value="Active" aria-label="Active Insurances">
              Active
            </ToggleButton>
            <ToggleButton value="Inactive" aria-label="Inactive insurances">
              Inactive
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            startIcon={<Add />}
            variant="outlined"
            disabled={disableInsuranceResponse}
            onClick={() => setShowInsuranceForm(true)}
          >
            Add Insurance
          </Button>
        </Box>
      </Box>

      <Divider />

      <BreadcrumbLayout.Paper>
        {paginatedInsurances ? (
          <AppTable
            columns={columns}
            data={paginatedInsurances}
            progressPending={false}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={filteredInsurances.length}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        ) : (
          <p>Loading notes...</p>
        )}
      </BreadcrumbLayout.Paper>

      {/* Form Drawer for Adding/Editing Insurance */}
      <FormDrawer
        title={
          selectedInsurance ? "Edit Vehicle Insurance" : "Add Vehicle Insurance"
        }
        open={showInsuranceForm}
        onClose={handleCancelInsurance}
      >
        <VehicleInsuranceForm
          isBusy={addingInsurance || updatingInsurance}
          vehicleInsurance={selectedInsurance}
          onSubmit={(data) =>
            selectedInsurance
              ? handleUpdateInsurance(data)
              : handleAddInsurance(data)
          }
          onCancel={handleCancelInsurance}
        />
      </FormDrawer>
    </>
  );
};

export default VehicleInsuranceTabs;
